import { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from './firebase';
import { useAuth } from './context/AuthContext';
import HomePage from './pages/HomePage';
import BrandingPage from './pages/BrandingPage';
import AboutPage from './pages/AboutPage';
import BlogListPage from './pages/BlogListPage';
import AllInstructors from './pages/AllInstructors';
import ArticleDetailPage from './pages/ArticleDetailPage';
import AdminLayout from './components/admin/AdminLayout';
import ProtectedRoute from './components/admin/ProtectedRoute';
import AdminOverview from './pages/admin/AdminOverview';
import AdminUsers from './pages/admin/AdminUsers';
import AdminStudents from './pages/admin/AdminStudents';
import AdminCustomers from './pages/admin/AdminCustomers';
import AdminCMS from './pages/admin/AdminCMS';
import AdminTestimonials from './pages/admin/AdminTestimonials';
import AdminArticles from './pages/admin/AdminArticles';
import AdminArticleForm from './pages/admin/AdminArticleForm';
import AdminOffer from './pages/admin/AdminOffer';
import AdminCustomerAnalysis from './pages/admin/AdminCustomerAnalysis';
import AdminWhatsApp from './pages/admin/AdminWhatsApp';
import AdminCourses from './pages/admin/AdminCourses';
import AdminCourseEditor from './pages/admin/AdminCourseEditor';
import AdminGradebook from './pages/admin/AdminGradebook';
import AdminReviews from './pages/admin/AdminReviews';
import AdminSettings from './pages/admin/AdminSettings';
import AdminStudentWorks from './pages/admin/AdminStudentWorks';
import AdminRequests from './pages/admin/AdminRequests';
import AdminRegistrations from './pages/admin/AdminRegistrations';
import AdminAssignments from './pages/admin/AdminAssignments';
import MaintenancePage from './pages/MaintenancePage';
import StudentWorks from './pages/StudentWorks';
import { CMSProvider } from './context/CMSContext';
import MetaPixel from './components/MetaPixel';
import AuthAction from './components/AuthAction'; // عدل المسار حسب ملفاتك
import Navbar from './components/Navbar';
import Footer from './components/Footer';

// Student / User Auth & Dashboard
import Login from './pages/Login';
import SignUp from './pages/SignUp';
import ForgotPassword from './pages/ForgotPassword';
import StudentProtectedRoute from './components/dashboard/StudentProtectedRoute';
import InstructorProtectedRoute from './components/dashboard/InstructorProtectedRoute';
import DashboardLayout from './components/dashboard/DashboardLayout';
import DashboardOverview from './pages/dashboard/DashboardOverview';
import CoursesMarketplace from './pages/dashboard/CoursesMarketplace';
import CourseDetail from './pages/dashboard/CourseDetail';
import CourseWatch from './pages/dashboard/CourseWatch';
import AccountSettings from './pages/dashboard/AccountSettings';
import StudentAssignments from './pages/dashboard/StudentAssignments';
import { AuthProvider } from './context/AuthContext';
import AdminSignUpSecret from './pages/AdminSignUpSecret';
import AdminCourseDetail from './pages/admin/AdminCourseDetail';
import InstructorOnboarding from './pages/InstructorOnboarding';
import InstructorDashboardLayout from './components/dashboard/InstructorDashboardLayout';
import InstructorOverview from './pages/instructor/InstructorOverview';
import InstructorCourses from './pages/instructor/InstructorCourses';
import InstructorStudents from './pages/instructor/InstructorStudents';
import InstructorAssignments from './pages/instructor/InstructorAssignments';
import InstructorSettings from './pages/instructor/InstructorSettings';
import InstructorReviews from './pages/instructor/InstructorReviews';
import InstructorPublicProfile from './pages/instructor/InstructorPublicProfile';
import { NotificationProvider } from './context/NotificationContext';
import NotificationsPage from './pages/NotificationsPage';
import { ToastProvider } from './context/ToastContext';
import ToastContainer from './components/ui/ToastContainer';

function AppInner({ theme, toggleTheme }) {
  const { isAdmin } = useAuth();
  const [isMaintenanceMode, setIsMaintenanceMode] = useState(false);

  useEffect(() => {
    const ref = doc(db, 'settings', 'general');
    const unsub = onSnapshot(ref, (snap) => {
      if (snap.exists()) {
        setIsMaintenanceMode(snap.data().isMaintenanceMode ?? false);
      }
    });
    return unsub;
  }, []);

  // Routing Guard: show maintenance page to non-admins when maintenance is ON
  if (isMaintenanceMode && !isAdmin) {
    return <MaintenancePage />;
  }

  return (
    <div className="min-h-screen font-sans flex flex-col relative transition-colors duration-500 bg-slate-50 dark:bg-zinc-950 text-slate-900 dark:text-white">
      <Routes>
        <Route path="/" element={<HomePage theme={theme} toggleTheme={toggleTheme} />} />
        <Route path="/TermsConditions" element={<HomePage theme={theme} toggleTheme={toggleTheme} defaultModal="terms" />} />
        <Route path="/privacypolicy" element={<HomePage theme={theme} toggleTheme={toggleTheme} defaultModal="privacy" />} />
        <Route path="/about" element={<AboutPage theme={theme} toggleTheme={toggleTheme} />} />
        <Route path="/creators" element={<StudentWorks theme={theme} toggleTheme={toggleTheme} />} />
        <Route path="/courses" element={<CoursesMarketplace isPublicView theme={theme} toggleTheme={toggleTheme} />} />
        <Route path="/branding" element={<BrandingPage theme={theme} toggleTheme={toggleTheme} />} />
        <Route path="/blog" element={<BlogListPage theme={theme} toggleTheme={toggleTheme} />} />
        <Route path="/blog/:id" element={<ArticleDetailPage theme={theme} toggleTheme={toggleTheme} />} />
        <Route path="/courses/:slugOrId" element={
          <div className="min-h-screen flex flex-col bg-slate-50 dark:bg-zinc-950 text-slate-900 dark:text-white transition-colors duration-500">
            <Navbar theme={theme} toggleTheme={toggleTheme} />
            <div className="pt-10 pb-16 flex-1 w-full relative z-10 transition-colors duration-500">
              <CourseDetail />
            </div>
            <Footer />
          </div>
        } />
        <Route path="/courses/:slugOrId/watch" element={
          <div className="min-h-screen bg-slate-50 dark:bg-zinc-950 text-slate-900 dark:text-white transition-colors duration-500">
            <CourseWatch />
          </div>
        } />
        <Route path="/auth/action" element={<AuthAction />} />
        {/* Student Auth & Dashboard Routes */}
        <Route path="/login" element={<Login />} />
        <Route path="/signup" element={<SignUp />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/admin/setup-secret" element={<AdminSignUpSecret />} />
        <Route path="/dashboard" element={<StudentProtectedRoute />}>
          <Route element={<DashboardLayout theme={theme} toggleTheme={toggleTheme} />}>
            <Route index element={<DashboardOverview />} />
            <Route path="courses" element={<CoursesMarketplace />} />
            <Route path="settings" element={<AccountSettings />} />
            <Route path="assignments" element={<StudentAssignments />} />
            <Route path="notifications" element={<NotificationsPage />} />
          </Route>
        </Route>

        {/* Instructor Routes */}
        <Route path="/inspirers" element={<AllInstructors theme={theme} toggleTheme={toggleTheme} />} />
        <Route path="/instructor-signup" element={<Navigate to="/signup" replace />} />
        <Route path="/instructor-onboarding" element={<InstructorOnboarding />} />
        <Route path="/instructor/:usernameOrId" element={<InstructorPublicProfile />} />

        <Route path="/instructor-dashboard" element={<InstructorProtectedRoute />}>
          <Route element={<InstructorDashboardLayout theme={theme} toggleTheme={toggleTheme} />}>
            <Route index element={<InstructorOverview />} />
            <Route path="courses" element={<InstructorCourses />} />
            <Route path="students" element={<InstructorStudents />} />
            <Route path="assignments" element={<InstructorAssignments />} />
            <Route path="reviews" element={<InstructorReviews />} />
            <Route path="settings" element={<InstructorSettings />} />
            <Route path="notifications" element={<NotificationsPage />} />
          </Route>
        </Route>

        {/* Admin Routes */}
        <Route path="/admin" element={<ProtectedRoute />}>
          <Route element={<AdminLayout theme={theme} toggleTheme={toggleTheme} />}>
            <Route index element={<Navigate to="overview" replace />} />
            <Route path="overview" element={<AdminOverview />} />
            <Route path="users" element={<AdminUsers theme={theme} />} />
            <Route path="students" element={<AdminStudents theme={theme} />} />
            <Route path="customers" element={<AdminCustomers />} />
            <Route path="cms" element={<AdminCMS />} />
            <Route path="testimonials" element={<AdminTestimonials />} />
            <Route path="articles" element={<AdminArticles />} />
            <Route path="articles/new" element={<AdminArticleForm />} />
            <Route path="articles/edit/:id" element={<AdminArticleForm />} />
            <Route path="offer" element={<AdminOffer />} />
            <Route path="customer-analysis" element={<AdminCustomerAnalysis />} />
            <Route path="whatsapp" element={<AdminWhatsApp />} />
            <Route path="courses" element={<AdminCourses />} />
            <Route path="courses/new" element={<AdminCourseEditor />} />
            <Route path="courses/edit/:id" element={<AdminCourseEditor />} />
            <Route path="gradebook" element={<AdminGradebook />} />
            <Route path="reviews" element={<AdminReviews />} />
            <Route path="courses/:id" element={<AdminCourseDetail />} />
            <Route path="settings" element={<AdminSettings />} />
            <Route path="student-works" element={<AdminStudentWorks />} />
            <Route path="requests" element={<AdminRequests />} />
            <Route path="registrations" element={<AdminRegistrations />} />
            <Route path="assignments" element={<AdminAssignments />} />
            <Route path="notifications" element={<NotificationsPage />} />
          </Route>
        </Route>
      </Routes>
    </div>
  );
}

function App() {
  const [theme, setTheme] = useState(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('theme') || (window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light');
    }
    return 'light';
  });

  useEffect(() => {
    const root = document.documentElement;
    if (theme === 'dark') {
      root.classList.add('dark');
    } else {
      root.classList.remove('dark');
    }
    localStorage.setItem('theme', theme);
  }, [theme]);

  const toggleTheme = () => {
    setTheme(prev => prev === 'light' ? 'dark' : 'light');
  };

  return (
    <AuthProvider>
      <NotificationProvider>
        <ToastProvider>
          <CMSProvider>
            <Router>
              <MetaPixel />
              <AppInner theme={theme} toggleTheme={toggleTheme} />
              <ToastContainer />
            </Router>
          </CMSProvider>
        </ToastProvider>
      </NotificationProvider>
    </AuthProvider>
  );
}

export default App;
